import { getId } from '../helpers/file';

const TOOL_VISITS_ENDPOINT = '/api/image-processing/tool-visits';
const TOOL_VISIT_COUNTS_ENDPOINT = `${TOOL_VISITS_ENDPOINT}/counts`;

export const IMAGE_PROCESSING_TOOL_KEYS = [
  'compress',
  'format-convert',
  'video-to-gif',
  'video-compressor',
  'watermark',
  'gif-maker',
] as const;

export type ImageProcessingToolKey = typeof IMAGE_PROCESSING_TOOL_KEYS[number];

export const IMAGE_PROCESSING_VISIT_BUCKETS = [
  '<10',
  '10+',
  '100+',
  '1k+',
  '10k+',
  '100k+',
  '1m+',
] as const;

export type ImageProcessingVisitBucket = typeof IMAGE_PROCESSING_VISIT_BUCKETS[number];
export type ImageProcessingToolVisitCounts = Partial<Record<ImageProcessingToolKey, ImageProcessingVisitBucket>>;

const visitBucketSet = new Set<string>(IMAGE_PROCESSING_VISIT_BUCKETS);

const sendWithFetch = (body: string): void => {
  try {
    void fetch(TOOL_VISITS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      credentials: 'same-origin',
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Visit reporting is best-effort and must never interrupt navigation.
  }
};

export const reportImageProcessingToolVisit = (toolKey: ImageProcessingToolKey): void => {
  let clientId: string | null;
  try {
    clientId = getId();
  } catch {
    return;
  }

  if (!clientId) {
    return;
  }

  const body = JSON.stringify({ toolKey, clientId });

  try {
    if (
      typeof navigator.sendBeacon === 'function' &&
      navigator.sendBeacon(
        TOOL_VISITS_ENDPOINT,
        new Blob([body], { type: 'application/json' })
      )
    ) {
      return;
    }
  } catch {
    // Fall through to a keepalive request when beacon submission is unavailable.
  }

  sendWithFetch(body);
};

export const getImageProcessingToolVisitCounts = async (): Promise<ImageProcessingToolVisitCounts> => {
  try {
    const response = await fetch(TOOL_VISIT_COUNTS_ENDPOINT, {
      method: 'GET',
      credentials: 'same-origin',
    });
    if (!response.ok) {
      return {};
    }

    const payload = await response.json();
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return {};
    }

    const responseBody = payload as Record<string, unknown>;
    const data = responseBody.data;
    if (
      responseBody.success !== true ||
      !data ||
      typeof data !== 'object' ||
      Array.isArray(data)
    ) {
      return {};
    }

    const counts: ImageProcessingToolVisitCounts = {};
    IMAGE_PROCESSING_TOOL_KEYS.forEach((toolKey) => {
      const bucket = (data as Record<string, unknown>)[toolKey];
      if (typeof bucket === 'string' && visitBucketSet.has(bucket)) {
        counts[toolKey] = bucket as ImageProcessingVisitBucket;
      }
    });
    return counts;
  } catch {
    return {};
  }
};
